<script setup lang="ts">
import useAsset from "@/apps/credit-line/helpers/useAsset";
import Button from "@/apps/credit-line/components/Button.vue";
import { useRouter } from "vue-router";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { getActivePhysicalCard } from "@/apps/credit-line/helpers/getActivePhysicalCard";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { AxiosError } from "axios";
import { onBeforeUnmount, ref, onMounted } from "vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Alert from "@/shared-components/common/Alert.vue";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";

const { summary, fetchSummary } = useCreditLineSummary();
const router = useRouter();
const cardReplaceImage = useAsset(
  "img/credit-line/credit-card/report-lost-damaged/replace-card.png"
);

const showAlert = ref<boolean>(false);
const textAlert = ref<string>("");
const notShipmentsAvailableModal = ref<boolean>(false);
const redWarningIcon = useAsset("img/credit-line/red_warning_icon.png");
const allowCardShipments = ref<boolean>(true);

const isLoading = ref(false);
const CreditLineService = new Service();
const emit = defineEmits(["next"]);

const handleContinueClick = () => {
  if (!allowCardShipments.value) {
    notShipmentsAvailableModal.value = true;
    return;
  }
  emit("next");
};
const handleClickClose = () => {
  router.back();
};

const selectedCard = getActivePhysicalCard(summary.value);
const { t } = useI18n();

const getFormSettings = async () => {
  try {
    const cardsSettings = await getSettings(SettingsCategory.CL_CARDS_PAGE);
    allowCardShipments.value =
      cardsSettings.allowCardShipments?.enabled ?? false;
  } catch (error) {
    console.error("Error fetching settings:", error);
  }
};

const handleFreezeCard = async () => {
  try {
    isLoading.value = true;
    const res = await CreditLineService.toggleCcStatus({
      deviceTag: AuthenticationHelper.getDeviceTag() || "",
      creditLineId: summary.value?.creditLineId || 0,
      status: "BLOCKED",
      lastFour: selectedCard?.lastFour || "",
    });
    if (res?.status === 200) {
      fetchSummary(true);
      handleClickClose();
    } else {
      textAlert.value = t("CARDSTATUSTOGGLEERROR");
      showAlert.value = true;
    }
  } catch (error) {
    textAlert.value = t("CARDSTATUSTOGGLEERROR");
    showAlert.value = true;
    ErrorHandler.handleNetworkErrorAndLog(error as AxiosError);
  } finally {
    isLoading.value = false;
  }
};

onBeforeUnmount(() => {
  isLoading.value = false;
});

onMounted(async () => {
  await getFormSettings();
});
</script>

<template>
  <LoadingOverlay :loading="isLoading" />
  <div class="container" v-if="!isLoading">
    <DownUpModal
      v-if="notShipmentsAvailableModal"
      :show-actions="true"
      :modal-header-title="t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.TITLE')"
      :modal-header-subtitle="
        t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.DESCRIPTION')
      "
      :modal-header-image="redWarningIcon"
      @click-overlay="notShipmentsAvailableModal = false"
    >
      <p class="text-center recommendation-text mb-4">
        {{
          t(
            "REPORT_LOST_OR_STOLEN_CARD.NOT_SHIPMENTS_AVAILABLE_MODAL.RECOMMENDATION"
          )
        }}
      </p>
      <Button class="w-100" @click="handleFreezeCard" :loading="isLoading">
        <span>{{
          t(
            "REPORT_LOST_OR_STOLEN_CARD.NOT_SHIPMENTS_AVAILABLE_MODAL.BUTTON_TEXT"
          )
        }}</span>
      </Button>
    </DownUpModal>
    <div class="header">
      <button class="header-btn" @click="handleClickClose">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
    <div class="body">
      <img :src="cardReplaceImage" width="180" height="178" />
      <p class="title">{{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_1.TITLE") }}</p>
      <p class="description">
        {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_1.DESCRIPTION") }}
      </p>
    </div>
    <div class="footer">
      <Alert
        v-if="showAlert"
        :message="textAlert"
        alertType="alert-danger"
        :closable="true"
        alertIcon="fa fa-exclamation-triangle"
        @onClose="showAlert = false"
      />
      <p class="footer-description">
        {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_1.ADVICE") }}
      </p>
      <p class="footer-action" @click="handleFreezeCard">
        {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_1.ADVICE_BTN") }}
      </p>
      <div class="container-btn">
        <Button @click="handleContinueClick">
          {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_1.FOOTER_ACTION") }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.container {
  text-align: center;
}
.header {
  padding: 0px 0px 20px 0px;
  display: flex;
  justify-content: flex-end;
}
.fa-xmark {
  font-size: 24px;
}
.body {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
  width: 100%;
}
.title {
  font-size: 20px;
  line-height: 29px;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 0px;
  color: #000000;
}
.description {
  margin-top: 12px;
  width: 70%;
  color: #595a5a;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
}
.footer {
  margin: 50px 0px 20px 0px;
}
.footer-description {
  color: #8b8b8b;
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 0px;
}
.footer-action {
  margin: 0px;
  color: #007c3b;
  font-size: 14px;
  line-height: 21px;
  font-weight: 600;
}
.container-btn {
  margin-top: 24px;
  padding: 0px 10px;
}
.header-btn {
  background: none;
  border: none;
}
.recommendation-text {
  color: #595a5a;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
}
</style>
